/**
 * The read side of `topics/`, as pure functions over a `DocumentView`.
 *
 * ADR 0012 shapes a read as `select…(view: DocumentView, …)`, and the hooks in `use-topics.ts` and
 * `use-grouped-topics.ts` are one `useMemo` each over these. Splitting them is what lets the
 * behaviour be tested against a real document (`document/testing.ts`) rather than through
 * `renderHook`.
 *
 * Every selector takes `DocumentView | null` and answers for a document that has not loaded, the
 * way `resolveLinks` does — the null check belongs with the read, not repeated at each caller.
 */

import type { DocumentView, EntryType, Topic } from '@/document'

export interface TopicWithUsage extends Topic {
  usageCount?: number
}

export interface GroupedTopics {
  journal: Topic[]
  task: Topic[]
  /** A null `entryType`, or one this grouping does not name */
  other: Topic[]
}

/**
 * Every live topic, defaults first and then alphabetical.
 *
 * **`entryType` distinguishes `undefined` from `null`.** Omitting it returns every kind's topics;
 * passing `null` returns the unscoped ones, which is a filter and not the absence of one. The
 * `<select>` in `topic-filter.tsx` produces exactly that `null`, so the distinction is load-bearing.
 */
export function selectTopics(
  view: DocumentView | null,
  entryType?: EntryType | null,
): Topic[] {
  if (!view) return []

  return Object.values(view.topics)
    .filter((topic) => {
      if (topic.archivedAt) return false
      if (entryType !== undefined) return topic.entryType === entryType
      return true
    })
    .sort((a, b) => {
      const aIsDefault = a.isDefault ?? false
      const bIsDefault = b.isDefault ?? false

      if (aIsDefault && !bIsDefault) return -1
      if (!aIsDefault && bIsDefault) return 1

      return a.name.localeCompare(b.name)
    })
}

/**
 * Every live topic with the number of live entries carrying it, most-used first.
 *
 * Ordered by count and then by name, which is a different order from `selectTopics` and stays
 * different: this list answers "what do I use", not "what exists".
 */
export function selectTopicsWithUsage(
  view: DocumentView | null,
): TopicWithUsage[] {
  if (!view) return []

  const usageCounts = new Map<string, number>()
  Object.values(view.entries).forEach((entry) => {
    if (entry.archivedAt) return
    entry.topicIds.forEach((topicId) => {
      usageCounts.set(topicId, (usageCounts.get(topicId) || 0) + 1)
    })
  })

  return Object.values(view.topics)
    .filter((topic) => !topic.archivedAt)
    .map((topic) => ({
      ...topic,
      usageCount: usageCounts.get(topic.id) || 0,
    }))
    .sort((a, b) => b.usageCount - a.usageCount || a.name.localeCompare(b.name))
}

/**
 * The live topic with this exact name, if there is one.
 *
 * Intentionally uses simple lowercase matching, NOT `normalizeTopicName()`. That function strips
 * every non-alphanumeric character (`"Hip-Hop"` → `"hiphop"`), which is too aggressive for an exact
 * lookup: this one needs `"Hip-Hop"` to match `"hip-hop"` but not `"hiphop"`.
 * `document/operations/topics.ts` normalises instead, because create-or-match wants the fuzzy form.
 */
export function findTopicByName(
  view: DocumentView | null,
  name: string,
  entryType?: EntryType | null,
): Topic | undefined {
  if (!view) return undefined

  const normalizedName = name.trim().toLowerCase()

  return Object.values(view.topics).find(
    (topic) =>
      !topic.archivedAt &&
      topic.name.toLowerCase() === normalizedName &&
      (entryType === undefined || topic.entryType === entryType),
  )
}

/**
 * Live topics split into the two kinds the filter renders as their own sections, plus the rest.
 */
export function selectGroupedTopics(view: DocumentView | null): GroupedTopics {
  const result: GroupedTopics = { journal: [], task: [], other: [] }

  selectTopics(view).forEach((topic) => {
    if (topic.entryType === 'journal') {
      result.journal.push(topic)
    } else if (topic.entryType === 'task') {
      result.task.push(topic)
    } else {
      result.other.push(topic)
    }
  })

  return result
}
